<template>
    <div class="data-form">

        <b-form autocomplete="off"
                @submit.stop.prevent="onSubmit">

            <div class="float-right">
                <!-- cancel button -->
                <b-btn class="mr-2 btn-cancel"
                       v-if="cancelRoute"
                       :to="cancelRoute">
                    {{ $i18n.gt('LABEL_CANCEL') }}
                </b-btn>

                <!-- submit button -->
                <b-btn type="submit"
                       variant="primary"
                       :disabled="$v.form && $v.form.$invalid">
                    {{ saveButtonLabel }}
                </b-btn>
            </div>

            <h1 v-if="title">{{ title }}</h1>

            <b-row :cols="$mq == 'sm' ? 1 : fieldGroups.length"
                   :class="$mq != 'sm' && fieldGroups.length == 1 ? 'mx-5' : ''">
                <!-- form inputs for specified fields, one group per column -->
                <b-col v-for="(fieldGroup,i) in fieldGroups" :key="i">
                    <div class="frame p-3">
                        <data-form-input v-for="(fieldName,j) in fieldGroup" :key="j"
                                         :$v="$v"
                                         :field-model="fieldModel"
                                         :field-name="fieldName"
                                         :model-instance="modelInstance">
                            <template v-slot:labelSlot="slotProps">
                                <slot name="labelSlot"
                                      v-bind:field="slotProps.field"
                                      v-bind:field-model="fieldModel"
                                      v-bind:field-name="fieldName"
                                      v-bind:form="$v.form"/>
                            </template>
                        </data-form-input>
                    </div>
                </b-col>
            </b-row>

            <!-- slot for custom preview content -->

            <slot name="preview"
                  :$v="$v"
                  :field-model="fieldModel"
                  :model-instance="modelInstance">
            </slot>
        </b-form>

    </div>
</template>

<script>

/**
 * A generic form for creating or updating an instance of a model, with validation.
 *
 * Requires a field model specifying the types and validation behaviors of the model's fields,
 * as well as a grouped list of names of fields to display on the form
 * (an array of arrays; each top-level array will be displayed as a column).
 *
 * Promises must also be provided to load or initialize the model instance, and to save it.
 *
 * The form will display the provided success/error messages after saving, and redirect to the provided
 * route if saving is successful.
 */

import Vue from 'vue'
import * as _ from 'lodash'
import Validation from '@/providers/Validation'
import {validationMixin} from 'vuelidate'

export default Vue.extend({
    name: 'DataForm',
    mixins: [validationMixin],
    props: {
        title: String,
        saveButtonLabel: String,
        fieldGroups: Array,
        fieldModel: Object,
        loadModelInstance: Function,
        onSubmitHandler: Function,
        submitSuccessMessage: String,
        submitErrorMessage: String,
        submitSuccessRoute: String,
        cancelRoute: String
    },
    data() {
        // NB: must compute the flattened field list in advance rather than directly in the return structure!
        // (otherwise buildFormModel will be given an empty field list)
        const fieldList = _.flatten(this.fieldGroups)
        const form = Validation.buildFormModel(this.fieldModel, fieldList)

        return {
            modelInstance: {},
            fieldList: fieldList,
            form: form,
            warnings: {}
        }
    },
    validations() {
        return Validation.buildFormValidations(this.fieldModel, this.fieldList)
    },
    methods: {
        async onSubmit(evt) {
            // this.$v.form.$touch()
            if (this.$v.form.$anyError) {
                return
            } else {
                Validation.extractFormModel(this.$v.form, this.fieldModel, this.modelInstance, this.fieldList)
                try {
                    await this.onSubmitHandler(this.modelInstance)
                    if (this.submitSuccessMessage) {
                        this.$toasted.show(this.submitSuccessMessage, {duration: 3000})
                    }
                    if (this.submitSuccessRoute) {
                        this.$router.push(this.submitSuccessRoute)
                    }
                } catch (e) {
                    if (this.submitErrorMessage) {
                        this.$utils.handleError(e, this.submitErrorMessage)
                    } else {
                        this.$utils.handleError(e, 'Form submission error')
                    }
                }
            }
        },
        dummyLoadModelInstance() {
            return new Promise((resolve, reject) => {
                const instance = {}
                resolve(instance)
            })
        },

    },
    mounted() {
        let load = this.loadModelInstance
        if (!this.loadModelInstance) {
            load = this.dummyLoadModelInstance
        }
        load()
            .then((modelInstance) => {
                this.modelInstance = modelInstance
                Validation.populateFormModel(this.form, this.fieldModel, this.modelInstance, this.fieldList)
            })
    }
})

</script>
